import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { getPurchases } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { decimateAndFormatNumber } from '../../../utils/number';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';

type PurchaseSortBy = 'date' | 'totalExclVat' | 'totalInclVat' | 'invoiceNumber';

const PURCHASES_HEADERS: ITableHeader[] = [
  {
    id: 'invoiceNumber',
    name: 'Factuur Nummer',
    sortable: true,
  },
  {
    id: 'date',
    name: 'Factuur Datum',
    sortable: true,
  },
  {
    id: 'supplier',
    name: 'Leverancier',
  },
  {
    id: 'totalInclVat',
    name: 'Totaal',
    sortable: true,
  },
];

const PurchasesPage = () => {
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');
  const [sortBy, setSortBy] = useState<PurchaseSortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getPurchases(
        variables.cursor,
        variables.take,
        {
          search: searchValue,
        },
        sortBy,
        sortOrder,
      );
    },
    [searchValue, sortBy, sortOrder],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue],
  );

  useEffect(() => {
    page.reset();
  }, [searchValue, sortBy, sortOrder]);

  const title = 'Aankopen';

  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Financieel',
                  to: '/internal/finance',
                }}
                currentItem={title}
              />
            }
            rightSide={
              <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                Nieuwe aankoop
              </LinkButton>
            }
          />
        }
        panel
        filters={
          <Input
            type="text"
            placeholder="Zoek in aankopen..."
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={PURCHASES_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as PurchaseSortBy | undefined);
            setSortOrder(order);
          }}
          mapData={(purchase) => {
            return [
              <Link to={`${purchase.id}`} className="link-text">
                {purchase.invoiceNumber || 'geen factuur nummer'}
              </Link>,
              formatDate(purchase.date),
              purchase.supplier.name,
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(purchase.totalInclVat, 2, {
                decimalSeperator: ',',
              })}`}</div>,
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default PurchasesPage;
